import React, { useEffect, useRef, useState } from 'react';
import './CierreDeTurno.scss';
import TitleView from '../../../components/atoms/TitleView/TitleView';
import { formateoBasico, revertirFormatoFecha } from '../../../utilities/formateos';
import { getDashboardData, getClientes, getCierresTurno, createCierreTurno, getCausas, getEfectos } from './CierreDeTurnoIntegracion';
import useGetDataUser from '../../../CustomHooks/useGetDataUser';
import Swal from 'sweetalert2';

import CARDS from './partes/CardsConfig';
import TablaCierres from './partes/TablaCierres';
import DetalleCierre from './partes/DetalleCierre';
import ModalIncidencias from './partes/ModalIncidencias';

/* ─── Componente principal ────────────────────────────────────────────────── */
const CierreDeTurno = () => {
    const hoy = formateoBasico(new Date());

    const ayer = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return formateoBasico(d); })();
    const userData = useGetDataUser();
    const dtModalRef = useRef(null);

    /* ── Filtros tabla principal ────────────────────────────────────────── */
    const [fechaInicio, setFechaInicio] = useState(ayer);
    const [fechaFin, setFechaFin] = useState(hoy);
    const [fechaFiltro, setFechaFiltro] = useState(hoy);

    /* ── Datos ──────────────────────────────────────────────────────────── */
    const [clientes, setClientes] = useState([]);
    const [registros, setRegistros] = useState([]);
    const [registrosLoading, setRegistrosLoading] = useState(false);

    /* ── Navegación ─────────────────────────────────────────────────────── */
    const [vista, setVista] = useState('tabla'); // 'tabla' | 'detalle'
    const [modoDetalle, setModoDetalle] = useState('nuevo'); // 'nuevo' | 'ver'
    const [registroSeleccionado, setRegistroSeleccionado] = useState(null);

    /* ── Cards incidencias en detalle ───────────────────────────────────── */
    const [incidenciasData, setIncidenciasData] = useState(null);
    const [incidenciasLoading, setIncidenciasLoading] = useState(false);

    /* ── Formulario de creación ─────────────────────────────────────────── */
    const [nota, setNota] = useState('');
    const [guardando, setGuardando] = useState(false);

    /* ── Modal de detalle de card ───────────────────────────────────────── */
    const [modalVisible, setModalVisible] = useState(false);
    const [cardActiva, setCardActiva] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [proveedorFilter, setProveedorFilter] = useState([]);
    const [causas, setCausas] = useState([]);
    const [efectos, setEfectos] = useState([]);

    /* ── Carga inicial ──────────────────────────────────────────────────── */
    useEffect(() => {
        getClientes(setClientes);
        getCausas().then((res) => {
            setCausas(res);
        })
       
    }, []);

    useEffect(() => {
        if (!clientes.length) return;
        getCierresTurno(setRegistros, setRegistrosLoading, fechaInicio, fechaFin);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientes]);

    /* ── Acciones tabla ─────────────────────────────────────────────────── */
    const handleBuscar = () => {
        getCierresTurno(setRegistros, setRegistrosLoading, fechaInicio, fechaFin);
    };

    const abrirNuevo = () => {
        setModoDetalle('nuevo');
        setNota('');
        setRegistroSeleccionado(null);
        setIncidenciasData(null);
        setVista('detalle');
        const ids = clientes.map((c) => c.code).join(',');
        getDashboardData(setIncidenciasData, setIncidenciasLoading, ids, fechaFiltro);
    };

    const abrirVer = (registro) => {
        setRegistroSeleccionado(registro);
        setModoDetalle('ver');
        setNota(registro.NOTA ?? '');
        setIncidenciasData(null);
        setVista('detalle');
        const ids = clientes.map((c) => c.code).join(',');
        getDashboardData(setIncidenciasData, setIncidenciasLoading, ids, revertirFormatoFecha(registro.FECHA));
    };

    const volver = () => {
        setVista('tabla');
        setIncidenciasData(null);
    };

    /* ── Guardar nuevo cierre ───────────────────────────────────────────── */
    const handleGuardar = async () => {
        if (!nota.trim()) return;
        setGuardando(true);
        await createCierreTurno({ nota, id_usuario: userData?.ID }).then((res) => {
            if (res.registrado === true) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Turno ya registrado',
                    text: res.message,
                });
                setGuardando(false);
            } else {
                Swal.fire({ icon: 'success', title: 'Cierre de turno guardado' });
                getCierresTurno(setRegistros, setRegistrosLoading, fechaInicio, fechaFin);
                volver();
                setGuardando(false);
            }
        });

    };

    /* ── Modal cards ────────────────────────────────────────────────────── */
    const abrirModal = (card) => {
        setCardActiva(card);
        setGlobalFilter('');
        setProveedorFilter([]);
        setModalVisible(true);
    };

    const cerrarModal = () => {
        setModalVisible(false);
        setProveedorFilter([]);
    };

    const datosModal = cardActiva && incidenciasData
        ? (cardActiva.getData
            ? cardActiva.getData({ incidencias: incidenciasData })
            : (incidenciasData[cardActiva.key] ?? []))
        : [];

    const datosModalFiltrados = cardActiva?.key === 'servicios_por_proveedor' && proveedorFilter.length
        ? datosModal.filter((r) => proveedorFilter.includes(r.NOMBRE_PROVEEDOR))
        : datosModal;

    const opcionesProveedor = cardActiva?.key === 'servicios_por_proveedor'
        ? [...new Set((incidenciasData?.servicios_por_proveedor ?? []).map((r) => r.NOMBRE_PROVEEDOR))]
            .map((p) => ({ label: p, value: p }))
        : [];

    /* ════════════════════════════════════════════════════════════════════ */

    
    return (
        <div className="CierreDeTurno">
            <TitleView title="Registro de Cierre de Turno" />

            {/* ══ Vista: Tabla principal ══════════════════════════════════ */}
            {vista === 'tabla' && (
                <TablaCierres
                    registros={registros}
                    registrosLoading={registrosLoading}
                    fechaInicio={fechaInicio}
                    setFechaInicio={setFechaInicio}
                    fechaFin={fechaFin}
                    setFechaFin={setFechaFin}
                    onBuscar={handleBuscar}
                    onNuevo={abrirNuevo}
                    onVer={abrirVer}
                />
            )}

            {/* ══ Vista: Detalle / Nuevo ══════════════════════════════════ */}
            {vista === 'detalle' && (
                <DetalleCierre
                    modoDetalle={modoDetalle}
                    registroSeleccionado={registroSeleccionado}
                    incidenciasData={incidenciasData}
                    incidenciasLoading={incidenciasLoading}
                    CARDS={CARDS}
                    onAbrirModal={abrirModal}
                    nota={nota}
                    setNota={setNota}
                    userData={userData}
                    guardando={guardando}
                    onGuardar={handleGuardar}
                    onVolver={volver}
                 
                 
                />
            )}

            {/* ── Modal detalle de card ──────────────────────────────────── */}
            <ModalIncidencias
                visible={modalVisible}
                onHide={cerrarModal}
                cardActiva={cardActiva}
                dtRef={dtModalRef}
                datosModalFiltrados={datosModalFiltrados}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                proveedorFilter={proveedorFilter}
                setProveedorFilter={setProveedorFilter}
                opcionesProveedor={opcionesProveedor}
                modoDetalle={modoDetalle}
                causas={causas}
                efectos={efectos}
                userData={userData}
                onEditarGuardado={() => {
                    const ids = clientes.map((c) => c.code).join(',');
                    const fecha = modoDetalle === 'ver'
                        ? revertirFormatoFecha(registroSeleccionado?.FECHA)
                        : fechaFiltro;
                    getDashboardData(setIncidenciasData, setIncidenciasLoading, ids, fecha);
                }}
            />
        </div>
    );
};

export default CierreDeTurno;
